import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Snackbar from '@mui/material/Snackbar'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import React, { useCallback, useEffect, useState } from 'react'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

import metaApi from '../../api/meta'
import Tedesco from '../../images/de.png'
import Danese from '../../images/dk.png'
import Italiana from '../../images/it.png'
import Olandese from '../../images/ne.png'
import Polacco from '../../images/pl.png'
import Inglese from '../../images/uk.png'
import SceltaLingua from './SceltaLingua'
import SocialPreview from './SocialPreview'

const LINGUA_TO_CODE = {
  1: 'italiano',
  2: 'inglese',
  3: 'tedesco',
  4: 'olandese',
  5: 'danese',
  6: 'polacco',
}

function MetaScheda({
  lingua,
  cambiaLingua,
  valoriMeta,
  cambiaValoriMeta,
  valoriMetaTitle,
  cambiaValoriMetaTitle,
  isModifica,
  favouriteImageUrl,
  resourceData,
}) {
  const [metaLoading, setMetaLoading] = useState(false)
  const [metaError, setMetaError] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false)

  // Fallback local state for when props are not provided
  const [localMetaTitle, setLocalMetaTitle] = useState({
    italiano: '',
    inglese: '',
    tedesco: '',
    olandese: '',
    danese: '',
    polacco: '',
  })

  // Sync local state when provided values change
  useEffect(() => {
    if (valoriMetaTitle) {
      setLocalMetaTitle(valoriMetaTitle)
    }
  }, [valoriMetaTitle])

  // Use provided values if handler is also provided, otherwise use local state
  const hasHandler = !!cambiaValoriMetaTitle
  const metaTitleValue = hasHandler ? valoriMetaTitle || localMetaTitle : localMetaTitle

  // Get current meta title for the selected language
  const currentMetaTitle =
    lingua == 1
      ? metaTitleValue.italiano || ''
      : lingua == 2
        ? metaTitleValue.inglese || ''
        : lingua == 3
          ? metaTitleValue.tedesco || ''
          : lingua == 4
            ? metaTitleValue.olandese || ''
            : lingua == 5
              ? metaTitleValue.danese || ''
              : metaTitleValue.polacco || ''

  // Get current meta description for the selected language
  const currentMetaDescription =
    lingua == 1
      ? valoriMeta?.italiano || ''
      : lingua == 2
        ? valoriMeta?.inglese || ''
        : lingua == 3
          ? valoriMeta?.tedesco || ''
          : lingua == 4
            ? valoriMeta?.olandese || ''
            : lingua == 5
              ? valoriMeta?.danese || ''
              : valoriMeta?.polacco || ''

  // State for social preview selection
  const [selectedSocial, setSelectedSocial] = useState('facebook')

  const handleSocialChange = (event, newValue) => {
    setSelectedSocial(newValue)
  }

  const handleMetaTitleChange = (e) => {
    if (cambiaValoriMetaTitle) {
      cambiaValoriMetaTitle(e)
    } else {
      const key = LINGUA_TO_CODE[lingua]
      if (key) setLocalMetaTitle((prev) => ({ ...prev, [key]: e.target.value }))
    }
  }

  const handleMetaDescriptionChange = (e) => {
    if (cambiaValoriMeta) cambiaValoriMeta(e)
  }

  const doGenerateMeta = useCallback(async () => {
    if (!resourceData || metaLoading) return
    const language = LINGUA_TO_CODE[lingua] || 'italiano'
    setMetaLoading(true)
    setMetaError(null)
    setConfirmOverwriteOpen(false)
    try {
      const response = await metaApi.generateMeta(resourceData, language)
      if (!response.ok || !response.data) {
        setMetaError(response.data?.error || 'Generation failed.')
        setSnackbarOpen(true)
        return
      }
      const { metaTitle, metaDescription } = response.data
      if (typeof metaDescription === 'string' && cambiaValoriMeta) {
        cambiaValoriMeta({ target: { value: metaDescription } })
      }
      if (typeof metaTitle === 'string') {
        if (cambiaValoriMetaTitle) {
          cambiaValoriMetaTitle({ target: { value: metaTitle } })
        } else {
          const key = language
          setLocalMetaTitle((prev) => ({ ...prev, [key]: metaTitle }))
        }
      }
    } catch (err) {
      setMetaError(err?.message || 'Generation failed.')
      setSnackbarOpen(true)
    } finally {
      setMetaLoading(false)
    }
  }, [resourceData, metaLoading, lingua, cambiaValoriMeta, cambiaValoriMetaTitle])

  const handleGenerateMeta = useCallback(() => {
    if (!resourceData || metaLoading) return
    const hasTitle = (currentMetaTitle || '').trim().length > 0
    const hasDescription = (currentMetaDescription || '').trim().length > 0
    if (hasTitle || hasDescription) {
      setConfirmOverwriteOpen(true)
      return
    }
    doGenerateMeta()
  }, [resourceData, metaLoading, currentMetaTitle, currentMetaDescription, doGenerateMeta])

  return (
    <>
      <div className={isModifica ? 'admin-container-lungo' : 'admin-container'} style={{ position: 'relative' }}>
        <SceltaLingua lingua={lingua} cambiaLingua={cambiaLingua}></SceltaLingua>
        {resourceData && (
          <Button
            variant="contained"
            size="medium"
            disabled={metaLoading}
            onClick={handleGenerateMeta}
            startIcon={metaLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              position: 'absolute',
              right: 0,
              bottom: 0,
              marginRight: '25px',
              marginBottom: '25px',
              gap: '8px',
            }}
          >
            {metaLoading ? 'Generating…' : 'Genera con AI'}
            <AutoAwesomeIcon />
          </Button>
        )}
      </div>
      <Dialog open={confirmOverwriteOpen} onClose={() => setConfirmOverwriteOpen(false)}>
        <DialogTitle>Aggiornamento dei campi meta</DialogTitle>
        <DialogContent>
          <DialogContentText>
            I campi meta attuali verranno aggiornati con i nuovi valori generati. Sei sicuro di voler continuare?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverwriteOpen(false)}>Cancel</Button>
          <Button onClick={doGenerateMeta} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <div className={isModifica ? 'admin-container-lungo' : 'admin-container'}>
        <Stack direction="row" spacing={2} paddingTop={2} paddingBottom={2} alignItems="center">
          <img
            src={
              lingua == 1
                ? Italiana
                : lingua == 2
                  ? Inglese
                  : lingua == 3
                    ? Tedesco
                    : lingua == 4
                      ? Olandese
                      : lingua == 5
                        ? Danese
                        : Polacco
            }
            className="admin-bandiera"
          ></img>
          <h3 className="admin-titoletto">META TITLE</h3>
        </Stack>
        <TextField
          id="meta-title"
          label="Meta title"
          variant="outlined"
          onChange={handleMetaTitleChange}
          value={
            lingua == 1
              ? metaTitleValue.italiano || ''
              : lingua == 2
                ? metaTitleValue.inglese || ''
                : lingua == 3
                  ? metaTitleValue.tedesco || ''
                  : lingua == 4
                    ? metaTitleValue.olandese || ''
                    : lingua == 5
                      ? metaTitleValue.danese || ''
                      : metaTitleValue.polacco || ''
          }
          size={'small'}
          fullWidth
          autoComplete="off"
          disabled={metaLoading}
          InputProps={metaLoading ? { readOnly: true } : undefined}
          sx={
            metaLoading
              ? {
                  animation: 'metaLoadingPulse 1.2s ease-in-out infinite',
                  '@keyframes metaLoadingPulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.45 },
                  },
                }
              : undefined
          }
        />
      </div>
      <div className={isModifica ? 'admin-container-lungo' : 'admin-container'}>
        <Stack direction="row" spacing={2} paddingTop={2} paddingBottom={2}>
          <img
            src={
              lingua == 1
                ? Italiana
                : lingua == 2
                  ? Inglese
                  : lingua == 3
                    ? Tedesco
                    : lingua == 4
                      ? Olandese
                      : lingua == 5
                        ? Danese
                        : Polacco
            }
            className="admin-bandiera"
          ></img>
          <h3 className="admin-titoletto">META DESCRIPTION</h3>
        </Stack>
        <TextField
          id="outlined-basic"
          label="Meta description"
          variant="outlined"
          onChange={handleMetaDescriptionChange}
          value={
            lingua == 1
              ? valoriMeta?.italiano || ''
              : lingua == 2
                ? valoriMeta?.inglese || ''
                : lingua == 3
                  ? valoriMeta?.tedesco || ''
                  : lingua == 4
                    ? valoriMeta?.olandese || ''
                    : lingua == 5
                      ? valoriMeta?.danese || ''
                      : valoriMeta?.polacco || ''
          }
          size={'small'}
          multiline={true}
          rows={5}
          fullWidth
          autoComplete="off"
          disabled={metaLoading}
          InputProps={metaLoading ? { readOnly: true } : undefined}
          sx={
            metaLoading
              ? {
                  animation: 'metaLoadingPulse 1.2s ease-in-out infinite',
                  '@keyframes metaLoadingPulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.45 },
                  },
                }
              : undefined
          }
        />
      </div>
      <div className={isModifica ? 'admin-container-lungo' : 'admin-container'}>
        <Stack direction="row" spacing={2} paddingTop={2} paddingBottom={2}>
          <img
            src={
              lingua == 1
                ? Italiana
                : lingua == 2
                  ? Inglese
                  : lingua == 3
                    ? Tedesco
                    : lingua == 4
                      ? Olandese
                      : lingua == 5
                        ? Danese
                        : Polacco
            }
            className="admin-bandiera"
          ></img>
          <h3 className="admin-titoletto">SOCIAL SNIPPET PREVIEW</h3>
        </Stack>
        <Tabs value={selectedSocial} onChange={handleSocialChange} sx={{ marginBottom: 2 }}>
          <Tab label="Facebook" value="facebook" />
          <Tab label="X (Twitter)" value="x" />
        </Tabs>
        <SocialPreview
          social={selectedSocial}
          imageUrl={favouriteImageUrl}
          title={currentMetaTitle}
          description={currentMetaDescription}
        />
      </div>
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
          {metaError || 'Generation failed.'}
        </Alert>
      </Snackbar>
    </>
  )
}

export default MetaScheda
